/* =============================================================================
 * ISMONT – Produkt-Datenblatt (PDF)
 * Datei: assets/ismont-datenblatt.js
 * Version: 1.3.0 – das PDF oeffnet in einem neuen Browser-Tab
 *
 * Ablauf beim Klick:
 *   1. SOFORT einen leeren Tab oeffnen (siehe Hinweis zum Popup-Blocker)
 *   2. Button sperren + Ladezustand anzeigen
 *   3. html2pdf.js (und optional qrcode.js) einmalig nachladen
 *   4. Erstelldatum + QR-Code in die Vorlage schreiben
 *   5. Warten, bis Logo und Produktbild geladen sind
 *   6. PDF rendern und im vorbereiteten Tab anzeigen
 *   7. Button zuruecksetzen (auch im Fehlerfall)
 * ========================================================================== */

(function () {
  'use strict';

  /* ---------------------------------------------------------------------
   * Konfiguration
   * Über window.ISMONT_DATASHEET_CONFIG vor dem Laden dieses Skripts
   * überschreibbar.
   * ------------------------------------------------------------------- */
  var DEFAULTS = {
    html2pdfSrc: 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.14.0/html2pdf.bundle.min.js',
    html2pdfFallbackSrc: 'https://cdn.jsdelivr.net/npm/html2pdf.js@0.10.3/dist/html2pdf.bundle.min.js',
    qrcodeSrc: 'https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js',
    enableQrCode: true,
    scale: 2,            // Rendering-Skalierung: 2 = scharf, 3 = schärfer/langsamer
    margin: 10,          // Seitenrand in mm
    imageQuality: 0.98,
    imageTimeout: 8000,  // ms, bis das Warten auf Bilder abgebrochen wird
    locale: 'de-DE',

    /* true  = PDF öffnet in einem neuen Browser-Tab (Kunde sieht es sofort,
     *         Herunterladen geht über die Leiste des PDF-Betrachters)
     * false = PDF wird direkt heruntergeladen (Verhalten bis Version 1.2) */
    openInBrowser: true,

    // Danach wird die Blob-URL freigegeben. Der offene Tab bleibt unberührt.
    blobLifetime: 120000
  };

  var CONFIG = Object.assign({}, DEFAULTS, window.ISMONT_DATASHEET_CONFIG || {});

  /* ---------------------------------------------------------------------
   * Übersetzungen
   * ------------------------------------------------------------------- */
  var STRINGS = Object.assign({
    de: {
      idle: '📄 Datenblatt anzeigen',
      loading: '⏳ Datenblatt wird erstellt...',
      success: 'Datenblatt wurde heruntergeladen.',
      opened: 'Datenblatt wurde in einem neuen Tab geöffnet.',
      warteSeite: 'Datenblatt wird erstellt …',
      popupBlockiert: 'Der Browser hat das neue Fenster blockiert — das Datenblatt wurde stattdessen heruntergeladen.',
      errorLib: 'Das PDF-Modul konnte nicht geladen werden. Bitte Werbeblocker deaktivieren und erneut versuchen.',
      errorGeneric: 'Das Datenblatt konnte nicht erstellt werden. Bitte versuchen Sie es erneut.'
    },
    en: {
      idle: '📄 View data sheet',
      loading: '⏳ Creating data sheet...',
      success: 'Data sheet downloaded.',
      opened: 'Data sheet opened in a new tab.',
      warteSeite: 'Creating data sheet …',
      popupBlockiert: 'Your browser blocked the new window — the data sheet was downloaded instead.',
      errorLib: 'The PDF module could not be loaded. Please disable your ad blocker and try again.',
      errorGeneric: 'The data sheet could not be created. Please try again.'
    }
  }, window.ISMONT_DATASHEET_I18N || {});

  function t(key) {
    var lang = (document.documentElement.lang || 'de').slice(0, 2).toLowerCase();
    var pack = STRINGS[lang] || STRINGS.de;
    return pack[key] || STRINGS.de[key] || '';
  }

  /* ---------------------------------------------------------------------
   * Skript einmalig laden (mit Promise-Cache)
   * ------------------------------------------------------------------- */
  var scriptCache = {};

  function loadScript(src) {
    if (scriptCache[src]) return scriptCache[src];

    scriptCache[src] = new Promise(function (resolve, reject) {
      var el = document.createElement('script');
      el.src = src;
      el.async = true;
      el.crossOrigin = 'anonymous';
      el.onload = function () { resolve(); };
      el.onerror = function () {
        delete scriptCache[src];               // erneuter Versuch bleibt möglich
        reject(new Error('Script konnte nicht geladen werden: ' + src));
      };
      document.head.appendChild(el);
    });

    return scriptCache[src];
  }

  function ensureHtml2Pdf() {
    if (typeof window.html2pdf === 'function') return Promise.resolve();

    return loadScript(CONFIG.html2pdfSrc)
      .catch(function () { return loadScript(CONFIG.html2pdfFallbackSrc); })
      .then(function () {
        if (typeof window.html2pdf !== 'function') {
          throw new Error('html2pdf global nicht verfügbar');
        }
      });
  }

  function ensureQrCode() {
    if (!CONFIG.enableQrCode) return Promise.resolve(false);
    if (typeof window.QRCode === 'function') return Promise.resolve(true);

    return loadScript(CONFIG.qrcodeSrc)
      .then(function () { return typeof window.QRCode === 'function'; })
      .catch(function () { return false; });      // QR-Code ist optional
  }

  /* ---------------------------------------------------------------------
   * Warten, bis alle Bilder im Datenblatt geladen sind
   * ------------------------------------------------------------------- */
  function waitForImages(root) {
    var images = Array.prototype.slice.call(root.querySelectorAll('img'));
    if (!images.length) return Promise.resolve();

    var jobs = images.map(function (img) {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();

      return new Promise(function (resolve) {
        var done = false;
        function finish() {
          if (done) return;
          done = true;
          img.removeEventListener('load', finish);
          img.removeEventListener('error', finish);
          resolve();
        }
        img.addEventListener('load', finish);
        img.addEventListener('error', finish);   // fehlerhafte Bilder blockieren nicht
        setTimeout(finish, CONFIG.imageTimeout);
      });
    });

    return Promise.all(jobs);
  }

  function applyLogoFallback(sheet) {
    var logo = sheet.querySelector('[data-ismont-logo]');
    var fallback = sheet.querySelector('[data-ismont-logo-fallback]');
    if (!logo || !fallback) return;

    if (logo.naturalWidth === 0) {
      logo.remove();
      fallback.hidden = false;
    }
  }

  /* ---------------------------------------------------------------------
   * Datenblatt vorbereiten: Erstelldatum und QR-Code
   * ------------------------------------------------------------------- */
  function stampDate(sheet) {
    var target = sheet.querySelector('[data-ismont-date]');
    if (!target) return;

    target.textContent = new Date().toLocaleDateString(CONFIG.locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  function renderQrCode(sheet, available) {
    var holder = sheet.querySelector('[data-ismont-qr]');
    if (!holder) return;

    var url = sheet.getAttribute('data-product-url');

    if (!available || !url) {
      holder.style.visibility = 'hidden';
      return;
    }

    if (holder.getAttribute('data-rendered') === 'true') return;

    try {
      /* global QRCode */
      new QRCode(holder, {
        text: url,
        width: 140,                 // doppelte Anzeigegröße = scharf im PDF
        height: 140,
        colorDark: '#000000',
        colorLight: '#ffffff',
        correctLevel: QRCode.CorrectLevel.M
      });
      holder.setAttribute('data-rendered', 'true');
    } catch (err) {
      holder.style.visibility = 'hidden';
    }
  }

  /* ---------------------------------------------------------------------
   * Button-Zustände
   * ------------------------------------------------------------------- */
  function setBusy(button, busy) {
    var label = button.querySelector('.ismont-ds-button__label') || button;

    if (busy) {
      button.dataset.originalLabel = label.textContent;
      label.textContent = t('loading');
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    } else {
      label.textContent = button.dataset.originalLabel || t('idle');
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  function setStatus(button, message, state) {
    var status = button.parentElement
      ? button.parentElement.querySelector('[data-ismont-datasheet-status]')
      : null;
    if (!status) return;

    if (!message) {
      status.hidden = true;
      status.textContent = '';
      status.removeAttribute('data-state');
      return;
    }

    status.textContent = message;
    status.setAttribute('data-state', state || 'info');
    status.hidden = false;

    if (state !== 'error') {
      setTimeout(function () {
        status.hidden = true;
        status.textContent = '';
      }, 6000);
    }
  }

  /* ---------------------------------------------------------------------
   * Anzeige im Browser
   *
   * WICHTIG: Der Tab muss SYNCHRON im Klick-Handler geoeffnet werden. Wird
   * window.open erst nach dem Rendern aufgerufen (also mehrere Sekunden
   * spaeter, ausserhalb der Nutzeraktion), blockiert ihn jeder Browser als
   * ungewolltes Popup. Deshalb wird sofort ein leerer Tab geoeffnet, mit
   * einer Wartemeldung gefuellt und erst danach die fertige PDF-Adresse
   * hineingegeben.
   * ------------------------------------------------------------------- */

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function oeffneWartetab(titel) {
    var tab;
    try {
      tab = window.open('', '_blank');
    } catch (err) {
      return null;
    }
    if (!tab) return null;   // vom Popup-Blocker verhindert

    try {
      tab.document.write(
        '<!DOCTYPE html><html lang="' + (document.documentElement.lang || 'de') + '">' +
        '<head><meta charset="utf-8"><title>' + escapeHtml(titel) + '</title>' +
        '<style>html,body{margin:0;height:100%;background:#f5f6f7;}' +
        'body{display:flex;align-items:center;justify-content:center;' +
        'font-family:Arial,Helvetica,sans-serif;color:#6b7280;font-size:15px;}' +
        'iframe{border:0;width:100%;height:100%;display:block;}</style></head>' +
        '<body><p data-warten>' + escapeHtml(t('warteSeite')) + '</p></body></html>'
      );
      tab.document.close();
    } catch (err) {
      /* Manche Browser erlauben document.write im leeren Tab nicht.
       * Der Tab bleibt dann leer und bekommt gleich die PDF-Adresse. */
    }
    return tab;
  }

  /* Zeigt das fertige PDF im vorbereiteten Tab. Bewusst in einem iframe:
   * so behaelt der Tab den Produktnamen als Titel statt der Blob-Kennung.
   * Der eingebaute PDF-Betrachter bringt seine eigene Schaltflaeche zum
   * Herunterladen mit. */
  function zeigePdfImTab(tab, url, titel) {
    try {
      if (tab.document && tab.document.body) {
        tab.document.title = titel;
        tab.document.body.innerHTML = '<iframe src="' + url + '" title="' + escapeHtml(titel) + '"></iframe>';
        return true;
      }
    } catch (err) {
      /* Kein Zugriff auf das Dokument – dann direkt anspringen */
    }

    try {
      tab.location.href = url;
      return true;
    } catch (err) {
      return false;
    }
  }

  /* ---------------------------------------------------------------------
   * Hauptfunktion: PDF erzeugen
   * ------------------------------------------------------------------- */
  function generatePdf(button) {
    var sheetId = button.getAttribute('data-sheet-target');
    var sheet = document.getElementById(sheetId);

    if (!sheet) {
      setStatus(button, t('errorGeneric'), 'error');
      return;
    }

    var filename = (button.getAttribute('data-filename') || 'datenblatt') + '.pdf';
    var titel = button.getAttribute('data-title') || filename.replace(/\.pdf$/, '');

    /* Der Tab wird JETZT geoeffnet, solange der Klick noch als Nutzeraktion
     * gilt. Ist openInBrowser aus oder blockiert der Browser, bleibt tab
     * null und es wird wie bisher heruntergeladen. */
    var tab = CONFIG.openInBrowser ? oeffneWartetab(titel) : null;
    var popupBlockiert = CONFIG.openInBrowser && !tab;

    setBusy(button, true);
    setStatus(button, null);

    ensureHtml2Pdf()
      .catch(function (err) {
        err.ismontReason = 'lib';
        throw err;
      })
      .then(function () {
        return ensureQrCode();
      })
      .then(function (qrAvailable) {
        stampDate(sheet);
        renderQrCode(sheet, qrAvailable);
        return waitForImages(sheet);
      })
      .then(function () {
        applyLogoFallback(sheet);

        var worker = window.html2pdf()
          .set({
            margin: CONFIG.margin,
            filename: filename,
            image: { type: 'jpeg', quality: CONFIG.imageQuality },
            html2canvas: {
              scale: CONFIG.scale,
              useCORS: true,          // Shopify-CDN liefert Access-Control-Allow-Origin: *
              allowTaint: false,
              backgroundColor: '#ffffff',
              logging: false,
              imageTimeout: CONFIG.imageTimeout
            },
            jsPDF: {
              unit: 'mm',
              format: 'a4',
              orientation: 'portrait',
              compress: true
            },
            /* 'css' respektiert page-break-inside aus dem Stylesheet,
             * 'legacy' die klassischen Umbruchregeln. Das fruehere
             * 'avoid-all' haette JEDES Element zusammengehalten — bei
             * vollstaendigen Produktbeschreibungen laeuft Text dann ueber
             * den Seitenrand hinaus, statt umzubrechen. */
            pagebreak: { mode: ['css', 'legacy'], avoid: ['.ismont-ds-table', '.ismont-ds-footer'] }
          })
          .from(sheet.querySelector('.ismont-ds-page') || sheet);

        // Kein Tab vorhanden (abgeschaltet oder blockiert): herunterladen
        if (!tab) {
          return worker.save().then(function () {
            return popupBlockiert ? 'blockiert' : 'geladen';
          });
        }

        return worker.outputPdf('bloburl').then(function (url) {
          var ok = zeigePdfImTab(tab, url, titel);

          /* Blob-URL nach einer Weile freigeben. Der Tab hat den Inhalt dann
           * laengst geladen; ohne das bliebe das PDF bis zum Seitenwechsel
           * im Speicher. */
          setTimeout(function () {
            try { URL.revokeObjectURL(url); } catch (e) { /* egal */ }
          }, CONFIG.blobLifetime);

          if (ok) return 'geoeffnet';

          // Tab war da, liess sich aber nicht befuellen → herunterladen
          try { tab.close(); } catch (e) { /* egal */ }
          return worker.save().then(function () { return 'geladen'; });
        });
      })
      .then(function (ergebnis) {
        if (ergebnis === 'geoeffnet') setStatus(button, t('opened'), 'success');
        else if (ergebnis === 'blockiert') setStatus(button, t('popupBlockiert'), 'info');
        else setStatus(button, t('success'), 'success');
      })
      .catch(function (err) {
        // Ein wartender Tab darf bei einem Fehler nicht offen stehen bleiben
        if (tab && !tab.closed) {
          try { tab.close(); } catch (e) { /* egal */ }
        }
        // eslint-disable-next-line no-console
        console.error('[ISMONT Datenblatt]', err);
        setStatus(
          button,
          err && err.ismontReason === 'lib' ? t('errorLib') : t('errorGeneric'),
          'error'
        );
      })
      .then(function () {
        setBusy(button, false);      // laeuft in jedem Fall (Erfolg wie Fehler)
      });
  }

  /* ---------------------------------------------------------------------
   * Initialisierung
   * Event-Delegation am document: funktioniert auch fuer Buttons, die per
   * Section-Rendering-API (Variantenwechsel, Quick-View) nachgeladen werden.
   * ------------------------------------------------------------------- */
  function init() {
    if (document.documentElement.hasAttribute('data-ismont-ds-init')) return;
    document.documentElement.setAttribute('data-ismont-ds-init', 'true');

    document.addEventListener('click', function (event) {
      var button = event.target.closest('[data-ismont-datasheet-trigger]');
      if (!button || button.disabled) return;

      event.preventDefault();
      generatePdf(button);
    });

    /* Performance: html2pdf.js beim ersten Hover/Fokus vorladen, damit der
       eigentliche Klick ohne Wartezeit auf das CDN auskommt. */
    ['mouseenter', 'focusin', 'touchstart'].forEach(function (evt) {
      document.addEventListener(evt, function (event) {
        var target = event.target;
        if (!target || typeof target.closest !== 'function') return;
        if (!target.closest('[data-ismont-datasheet-trigger]')) return;
        ensureHtml2Pdf().catch(function () { /* stiller Fehlschlag */ });
      }, { capture: true, passive: true, once: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
